import voiceService from './voiceService';

/**
 * Chef Summaries Service
 * Handles end-of-session Q&A flow to populate structured session reports
 */
class ChefSummariesService {
    constructor() {
        this.isActive = false;
        this.currentQuestionIndex = 0;
        this.responses = {};
        this.onComplete = null;
        this.onProgress = null;
        
        // Q&A flow configuration
        this.questions = [
            {
                id: 'key_moments',
                type: 'highlights',
                question: "What were the key moments or successes during this cooking session?",
                followUp: "Tell me about any special techniques, perfect timing, or great results you achieved.",
                field: 'highlights'
            },
            {
                id: 'issues_challenges',
                type: 'timeline',
                question: "Did you encounter any issues or challenges while cooking?",
                followUp: "What happened and how did you handle it?",
                field: 'timeline',
                status: 'needs_attention'
            },
            {
                id: 'final_result',
                type: 'outcome',
                question: "How did the final dish turn out?",
                followUp: "Rate your satisfaction from 1 to 5 and tell me about the taste, texture, and presentation.",
                field: 'final_outcome'
            }
        ];
    }

    /**
     * Start the Chef Summaries Q&A flow
     * @param {Object} options - Configuration options
     * @param {Function} options.onComplete - Callback when Q&A is complete
     * @param {Function} options.onProgress - Callback for progress updates
     */
    async start(options = {}) {
        if (this.isActive) {
            console.warn('Chef Summaries Q&A already active');
            return;
        }

        this.isActive = true;
        this.currentQuestionIndex = 0;
        this.responses = {};
        this.onComplete = options.onComplete;
        this.onProgress = options.onProgress;

        console.log('Starting Chef Summaries Q&A flow');
        
        // Start with first question
        await this.askQuestion();
    }

    /**
     * Ask the current question
     */
    async askQuestion() {
        if (!this.isActive || this.currentQuestionIndex >= this.questions.length) {
            await this.complete();
            return;
        }

        const question = this.questions[this.currentQuestionIndex];
        console.log(`Asking question ${this.currentQuestionIndex + 1}/${this.questions.length}:`, question.question);

        // Notify progress
        if (this.onProgress) {
            this.onProgress({
                currentQuestion: this.currentQuestionIndex + 1,
                totalQuestions: this.questions.length,
                question: question.question,
                questionId: question.id
            });
        }

        // Speak the question
        await this.speak(question.question);
        
        // Start listening for response
        this.startListening();
    }

    /**
     * Start listening for user response
     */
    startListening() {
        if (!this.isActive) return;

        // Set up voice service callbacks
        voiceService.setOnResult((transcript, isTriggered) => {
            if (!isTriggered) {
                // Only process non-triggered speech (direct responses)
                this.handleResponse(transcript);
            }
        });

        voiceService.setOnError((error) => {
            console.error('Voice recognition error during Chef Summaries:', error);
            // Continue to next question on error
            this.nextQuestion();
        });

        // Start voice recognition
        voiceService.startListening();
    }

    /**
     * Handle user response to current question
     * @param {string} transcript - User's spoken response
     */
    async handleResponse(transcript) {
        if (!this.isActive) return;

        const question = this.questions[this.currentQuestionIndex];
        console.log(`Response to "${question.question}":`, transcript);

        // Store the response
        this.responses[question.id] = {
            question: question.question,
            response: transcript,
            timestamp: new Date().toISOString(),
            type: question.type,
            field: question.field
        };

        // Track Q&A interaction in session report
        this.trackQAInteraction(question.question, transcript, -1, {
            questionId: question.id,
            questionType: question.type
        });

        // Acknowledge the response
        await this.speak("Thank you for that information.");

        // Move to next question
        this.nextQuestion();
    }

    /**
     * Move to the next question
     */
    async nextQuestion() {
        this.currentQuestionIndex++;
        
        if (this.currentQuestionIndex < this.questions.length) {
            // Small delay before next question
            setTimeout(() => {
                this.askQuestion();
            }, 1000);
        } else {
            await this.complete();
        }
    }

    /**
     * Complete the Q&A flow and generate structured report
     */
    async complete() {
        if (!this.isActive) return;

        console.log('Chef Summaries Q&A completed. Responses:', this.responses);
        
        // Generate structured report from responses
        const structuredReport = this.generateStructuredReport();
        
        // Notify completion
        if (this.onComplete) {
            this.onComplete(structuredReport);
        }

        // Cleanup
        this.cleanup();
    }

    /**
     * Generate structured session report from Q&A responses
     * @returns {Object} Structured session report
     */
    generateStructuredReport() {
        const report = {
            metadata: {
                generated_by: 'chef_summaries',
                generated_at: new Date().toISOString(),
                version: '1.0'
            },
            timeline: [],
            highlights: [],
            final_outcome: {
                rating: null,
                comments: '',
                dish_photo: null
            }
        };

        // Process each response
        Object.values(this.responses).forEach(response => {
            switch (response.type) {
                case 'highlights':
                    // Extract key moments as highlights
                    if (response.response.trim()) {
                        report.highlights.push({
                            id: `highlight_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                            type: 'success',
                            description: response.response,
                            timestamp: response.timestamp,
                            source: 'chef_summaries'
                        });
                    }
                    break;

                case 'timeline':
                    // Extract issues as timeline entries
                    if (response.response.trim()) {
                        report.timeline.push({
                            id: `timeline_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                            timestamp: response.timestamp,
                            phase: 'cooking',
                            notes: response.response,
                            status: 'needs_attention',
                            source: 'chef_summaries'
                        });
                    }
                    break;

                case 'outcome':
                    // Extract final result information
                    if (response.response.trim()) {
                        // Try to extract rating from response
                        const ratingMatch = response.response.match(/(\d+)/);
                        if (ratingMatch) {
                            const rating = parseInt(ratingMatch[1]);
                            if (rating >= 1 && rating <= 5) {
                                report.final_outcome.rating = rating;
                            }
                        }
                        
                        report.final_outcome.comments = response.response;
                    }
                    break;
            }
        });

        return report;
    }

    /**
     * Speak text using browser speech synthesis
     * @param {string} text - Text to speak
     */
    async speak(text) {
        return new Promise((resolve) => {
            if ('speechSynthesis' in window) {
                window.speechSynthesis.cancel();
                const utterance = new SpeechSynthesisUtterance(text);
                utterance.rate = 0.9;
                utterance.pitch = 1;
                utterance.volume = 0.8;
                
                utterance.onend = () => {
                    resolve();
                };
                
                utterance.onerror = () => {
                    resolve(); // Resolve even on error to continue flow
                };
                
                window.speechSynthesis.speak(utterance);
            } else {
                // No speech synthesis available, resolve immediately
                resolve();
            }
        });
    }

    /**
     * Process voice input with AI to make it coherent
     * @param {string} transcript - Raw voice transcript
     * @param {string} sessionId - Current session ID
     * @param {string} userToken - User auth token
     * @returns {Promise<string>} Formatted text
     */
    async processVoiceInput(transcript, sessionId, userToken) {
        if (!transcript || !transcript.trim()) {
            return '';
        }

        if (!sessionId || !userToken) {
            console.warn('Cannot process voice: missing session ID or token');
            return transcript;
        }

        try {
            const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
            const response = await fetch(
                `${API_BASE_URL}/api/sessions/${sessionId}/process-voice`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${userToken}`
                    },
                    body: JSON.stringify({
                        transcript,
                        context: {
                            phase: 'summary',
                            stepIndex: this.currentQuestionIndex
                        }
                    })
                }
            );

            if (!response.ok) {
                throw new Error('Failed to process voice input');
            }

            const result = await response.json();
            const processedText = result.formattedText || transcript;
            
            // Track voice processing interaction
            this.trackVoiceProcessingInteraction(transcript, processedText, -1, {
                questionIndex: this.currentQuestionIndex,
                phase: 'summary'
            });
            
            return processedText;

        } catch (error) {
            console.error('Error processing voice input:', error);
            return transcript;
        }
    }

    /**
     * Detect stop intent from user response
     * @param {string} text - User's text response
     * @param {string} sessionId - Current session ID
     * @param {string} userToken - User auth token
     * @returns {Promise<Object>} Stop intent detection result
     */
    async detectStopIntent(text, sessionId, userToken) {
        if (!text || !text.trim()) {
            return {
                shouldStop: false,
                confidence: 0
            };
        }

        if (!sessionId || !userToken) {
            // Fallback to simple pattern matching
            return this.simpleStopDetection(text);
        }

        try {
            const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
            const response = await fetch(
                `${API_BASE_URL}/api/sessions/${sessionId}/process-voice`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${userToken}`
                    },
                    body: JSON.stringify({
                        transcript: text,
                        context: {
                            phase: 'summary',
                            stepIndex: this.currentQuestionIndex
                        }
                    })
                }
            );

            if (!response.ok) {
                throw new Error('Failed to detect stop intent');
            }

            const result = await response.json();
            return result.stopIntent || { shouldStop: false, confidence: 0 };

        } catch (error) {
            console.error('Error detecting stop intent:', error);
            return this.simpleStopDetection(text);
        }
    }

    /**
     * Simple stop intent detection (fallback)
     */
    simpleStopDetection(text) {
        const lowerText = text.toLowerCase().trim();
        const stopPhrases = [
            'no', 'nope', 'nothing', 'nothing else', 'nothing more',
            "that's it", "that's all", "done", "i'm done", "im done",
            "all set", "that's everything"
        ];

        const hasStopPhrase = stopPhrases.some(phrase => 
            lowerText === phrase || 
            lowerText.startsWith(phrase + ' ') || 
            lowerText.endsWith(' ' + phrase)
        );

        return {
            shouldStop: hasStopPhrase,
            confidence: hasStopPhrase ? 0.8 : 0.2
        };
    }

    /**
     * Stop the Q&A flow
     */
    stop() {
        if (!this.isActive) return;

        console.log('Stopping Chef Summaries Q&A flow');
        this.cleanup();
    }

    /**
     * Cleanup resources
     */
    cleanup() {
        this.isActive = false;
        this.currentQuestionIndex = 0;
        this.responses = {};
        this.onComplete = null;
        this.onProgress = null;
        
        // Clear voice service callbacks
        voiceService.setOnResult(null);
        voiceService.setOnError(null);
        
        // Stop voice recognition
        voiceService.stopListening();
        
        // Cancel any ongoing speech
        if ('speechSynthesis' in window) {
            window.speechSynthesis.cancel();
        }
    }

    /**
     * Track voice processing interaction in session report
     * @param {string} originalTranscript - Original voice transcript
     * @param {string} processedTranscript - AI-processed transcript
     * @param {number} stepIndex - Current step index
     * @param {Object} context - Additional context
     */
    trackVoiceProcessingInteraction(originalTranscript, processedTranscript, stepIndex = -1, context = {}) {
        // Import sessionReport dynamically to avoid circular dependency
        import('./sessionReport').then(({ default: sessionReport }) => {
            sessionReport.addVoiceProcessingInteraction(originalTranscript, processedTranscript, stepIndex, {
                ...context,
                phase: 'summary'
            });
        }).catch(error => {
            console.warn('Could not track voice processing interaction:', error);
        });
    }

    /**
     * Track Q&A interaction in session report
     * @param {string} question - The question asked
     * @param {string} response - The user's response
     * @param {number} stepIndex - Current step index
     * @param {Object} context - Additional context
     */
    trackQAInteraction(question, response, stepIndex = -1, context = {}) {
        // Import sessionReport dynamically to avoid circular dependency
        import('./sessionReport').then(({ default: sessionReport }) => {
            sessionReport.addChefQuestionInteraction(question, response, stepIndex, {
                ...context,
                phase: 'summary'
            });
        }).catch(error => {
            console.warn('Could not track Q&A interaction:', error);
        });
    }

    /**
     * Check if Chef Summaries is enabled for user
     * @param {Object} userProfile - User profile object
     * @returns {boolean} Whether Chef Summaries is enabled
     */
    static isEnabled(userProfile) {
        return userProfile?.agent_preferences?.chef_summaries === true;
    }
}

// Export singleton instance
export const chefSummaries = new ChefSummariesService();
export default chefSummaries;
